import type {
  PortalBuildingsSortBy,
  PortalBuildingsSortDir,
} from "@/api/portal";

/**
 * Column geometry for the /portal Buildings table: `Main.dc.html`'s
 * `.bldg-grid`, VERBATIM.
 *
 *   grid-template-columns: 38px minmax(0,1fr) 86px 104px 116px 128px 18px;
 *   gap: 13px;
 *
 * KEEP THE 13px GAP. At `gap-6`: 490 fixed + 144 gaps + 8 padding = 642 before
 * BUILDING gets a pixel, so 822 at BUILDING's 180px floor, which scrolls at
 * every docked width from 1024 to 1279. At `gap-[13px]`: 490 + 78 + 8 = 576,
 * so 756 at the floor.
 *
 * NO `display`. The header prepends `hidden sm:grid`; the data row prepends a
 * bare `grid` (mounted only at ≥sm via `useMediaQuery`). Below sm the row is a
 * stacked card.
 *
 * NO `align-items` EITHER. Measured on `Main.dc.html` at 1440x1200 / dpr 1.25,
 * three rows in this one grid align three ways: column head `normal`
 * (stretch), band header `baseline`, data row `center`. An `items-center`
 * default collapsed `#`, `BUILDING` and `BIN` to an 11px box centred in the
 * 22px row while the two-line labels (`UNRESOLVED 90D+`, `OWED BY YOU`,
 * `CODE-DRIVEN EST.`) filled it and stayed at the top: a 5.5px break in one
 * header row. So the band header no longer needs its `!items-baseline`, and
 * the DATA row keeps `items-center` because a 12px live chevron baselined
 * against a 16px serif address sits visibly low.
 */
export const PORTAL_TABLE_GRID_TEMPLATE =
  "[grid-template-columns:38px_minmax(0,1fr)_86px_104px_116px_128px_18px] gap-[13px]";

/**
 * THE COLUMN GUTTER: 4px, and ONE ALIGNMENT CONTRACT. The seven tracks resolve
 * against the content box, so the column head, data row, band headers,
 * off-page band and table foot must share the inset or the head stops sitting
 * over its column. It was six call sites, one in
 * `PortalBuildingsTableHeader.tsx`; changing five of six passes every unit test
 * and shows only in a pixel read. `PORTAL_TABLE_MIN_WIDTH_PX` spends `8` on it
 * too, so floor and gutter are one decision.
 *
 * THE VERTICAL HALF IS DELIBERATELY ABSENT: the board insets its head row
 * `4px 4px 2px` and its data rows `5px 4px 2px`. Only the shared half is a
 * token.
 */
export const PORTAL_TABLE_GUTTER = "px-[4px]";

/**
 * The same gutter, `sm:`-gated, for the table FOOT, which runs edge-to-edge on
 * the phone card stack (`px-0`).
 *
 * A SECOND LITERAL, NOT `` `sm:${PORTAL_TABLE_GUTTER}` ``: Tailwind scans
 * source TEXT, so an interpolated arbitrary value compiles, type-checks,
 * renders the right className and emits no CSS. Same trap as
 * `PORTAL_TABLE_MIN_WIDTH_CLASS`.
 */
export const PORTAL_TABLE_GUTTER_SM = "sm:px-[4px]";

/**
 * Min-width before the columns crush, on the `role="grid"` container inside the
 * table-wrap's `overflow-x-auto`, so header and rows scroll as one board and
 * the page body never scrolls sideways
 * (`feedback_responsive_all_screen_sizes`).
 *
 * 756 IS DERIVED:
 *   490  six fixed tracks (38 + 86 + 104 + 116 + 128 + 18)
 *   78   six 13px gutters
 *   8    the row's own `px-[4px]`; rows live INSIDE this container
 *   ---
 *   576  with BUILDING at zero
 *   180  the 1fr BUILDING floor
 *   ---
 *   756
 *
 * THE 180 IS MEASURED. The old 356 was measured against `"112 Madison Avenue,
 * New York, NY, 10016"` (320px of ink). The row now prints
 * {@link buildingStreetLine}'s street line only (`112 Madison Avenue`, 132px),
 * city/state/zip on `title`, BIN in its own 86px track, so the widest live
 * datum is `61-17 WOODSIDE AVE.` plus the exception chip. Re-measured the same
 * way (min-width pinned, `scrollWidth` vs `clientWidth` on all six demo rows):
 * 180, and nothing ellipsises at 756. The scroller is `layoutVW − 66` below
 * `lg` and `layoutVW − 354` above, so 756 fits the docked curve down to C=756;
 * the old 840 scrolled 183px at a 1024 window where this scrolls 99.
 * `BAND_WITNESS_BREAKPOINTS` (`responsiveHarness.ts`) still sweeps 896 + 1152.
 *
 * KEEP THE `sm:` PREFIX. Below 640 there is deliberately NO min-width: the row
 * swaps to a stacked card at `(max-width: 639.98px)`, a JS media query
 * subscribed ONCE on `PortalBuildingsTable`
 * (`useMediaQuery(PORTAL_ROW_COMPACT_MEDIA_QUERY)`, passed down as `compact`;
 * one per row is fifty `matchMedia` listeners at a page size of 50). Dropping
 * the prefix floors a card layout at 756px and hands the phone a sideways
 * scroll. The `.98` complements `sm:`'s `(min-width: 640px)`: at a fractional
 * viewport in (639, 640), from 125%/150% display scaling or zoom, a bare
 * `(max-width: 639px)` matches NOTHING and the row rendered as a headerless
 * five-column grid with no min-width. Reproduced live at 639.0000095.
 *
 * Border-box vs content-box is MOOT: the board states its own floor
 * border-box, 8px of row padding included.
 */
export const PORTAL_TABLE_MIN_WIDTH_CLASS = "sm:min-w-[756px]";

/**
 * The same number as a number, for the geometry regression test. The class
 * above must repeat it as a LITERAL (Tailwind scans source text), so the test
 * pins the two together.
 */
export const PORTAL_TABLE_MIN_WIDTH_PX = 756;

/** The BUILDING (1fr) column's floor at `PORTAL_TABLE_MIN_WIDTH_PX`. */
export const PORTAL_TABLE_BUILDING_FLOOR_PX = 180;

/**
 * Where the row recomposes from a stacked card to the `role="grid"` row.
 * Complement of Tailwind's `sm:` (`min-width: 640px`); see the `.98` note
 * above, a bare 639 leaves a gap on fractional viewports.
 */
export const PORTAL_ROW_COMPACT_MEDIA_QUERY = "(max-width: 639.98px)";

/**
 * The street line alone, as the board prints it: `1166 Elton Street`, not
 * `1166 Elton Street, Brooklyn, NY, 11239`. On an all-NYC portfolio the
 * borough/state/zip repeats on every row, in the only column that has to flex,
 * and it was 188px of the old 356px floor. Nothing is lost: the full string
 * stays on the row's `title` and the Link's `aria-label`, and BIN is a column
 * now. A string with no comma comes back whole. This trims a known suffix; it
 * does not parse an address.
 */
export const buildingStreetLine = (address: string): string => {
  const cut = address.indexOf(",");
  if (cut <= 0) return address;
  return address.slice(0, cut).trim() || address;
};

/**
 * WHAT THE TABLE IS CALLED: printed once, spoken once, written here once.
 *
 * The board's `BUILDINGS, RANKED BY WHAT YOU OWE` is true only of the ungrouped
 * branch. The live table partitions by tier (`RISK_BAND_ORDER`) and keeps the
 * money order inside each block, and a heading claiming only the money order
 * over a grouped table shipped once and was caught in review, so the grouped
 * branch states both orderings.
 *
 * THE SPOKEN FORM IS LONGER ON PURPOSE and is a different string: the
 * `role="grid"` accessible name, read once before fourteen rows with no colhead
 * tier to lean on, costing zero visible ink. Both live here so the two cannot
 * drift into describing different tables.
 */
export const BUILDINGS_TABLE_NAME = {
  grouped: {
    printed: "Buildings by risk, then by what you owe",
    spoken:
      "Buildings, grouped by risk and ranked by what you owe within each group",
  },
  flat: {
    // The board's own six words, true whenever nothing is grouped.
    printed: "Buildings, ranked by what you owe",
    spoken: "Buildings, ranked by what you owe",
  },
} as const;

/** One sortable key and the word the affordance prints for it. */
export interface PortalSortColumn {
  key: PortalBuildingsSortBy;
  label: string;
}

/**
 * THE TWO MONEY COLUMNS: the sorts the table's column heads carry, and the
 * WHOLE list. No gate, no portfolio-size parameter, no third entry.
 *
 * *** THE BASIS OF `OWED BY YOU` MUST NOT MOVE WITH THE WORD. *** `OATH`
 * under-named the column: the rail two bands up reads ACTIVE AT OATH + DOB, so
 * a column headed OATH appeared to exclude the DOB money it sums. Subtraction
 * note 2 renamed it while ALSO re-basing it onto
 * `attribution_split.owed_total`, which is populated on the fixture and 0 on
 * every row of the live tenant, where `enforcementExposure` sums to exactly
 * $285,202 across the six rows and so reconciles to the rail cell above.
 * Re-basing would silently zero the column and break the one addition the
 * concentration line at the table head lets a reader check. Finish the rename
 * the day `owed_total` is populated, not before.
 *
 * The RISK COLUMN and the RISK SORT are both GONE (subtraction note 4: a 132px
 * track repeating the tier on all fourteen rows under band headers that state
 * it). The seven tracks and `PORTAL_TABLE_MIN_WIDTH_PX` derive from its
 * absence, and `PortalBuildingsTable` draws the tier grouping itself.
 */
export const PORTAL_MONEY_SORT_COLUMNS = [
  { key: "enforcement", label: "OWED BY YOU" },
  { key: "projection", label: "CODE-DRIVEN EST." },
] as const satisfies ReadonlyArray<PortalSortColumn>;

/**
 * The sort this page mounts with, and the one the route validator fills in.
 * Named rather than inlined because two call sites compare against it: the
 * rehydrate guard, which must tell "the reader asked for enforcement" from
 * "nobody asked", and the fallback when a stored sort is no longer on offer. A
 * literal in either drifts from the validator, silently.
 */
export const PORTAL_DEFAULT_SORT = {
  sortBy: "enforcement",
  sortDir: "desc",
} as const satisfies {
  sortBy: PortalBuildingsSortBy;
  sortDir: PortalBuildingsSortDir;
};

/**
 * Shared by the desktop header buttons and the mobile Sort control: a NEW
 * column takes its default direction (both `desc`, money high-first); the
 * ACTIVE column flips. One formula, no drift between the two surfaces.
 */
export const nextSort = (
  clicked: PortalBuildingsSortBy,
  current: { sortBy: PortalBuildingsSortBy; sortDir: PortalBuildingsSortDir },
): { sortBy: PortalBuildingsSortBy; sortDir: PortalBuildingsSortDir } => {
  if (clicked === current.sortBy) {
    return {
      sortBy: clicked,
      sortDir: current.sortDir === "asc" ? "desc" : "asc",
    };
  }
  return { sortBy: clicked, sortDir: "desc" };
};

/** The pair that describes an order: which column, which way. */
export interface PortalStoredSort {
  sortBy: PortalBuildingsSortBy;
  sortDir: PortalBuildingsSortDir;
}

/**
 * PER COMPANY, which is the whole reason this is a function and not a constant.
 * The sort belongs to the PORTFOLIO, not the browser, and one shared key would
 * hand a staff account moving between tenants the previous tenant's order on
 * the first paint of the next one.
 */
export const portalSortStorageKey = (companyId: string): string =>
  `codeclear.portal.buildingsSort.${companyId}`;

/**
 * The reader's last chosen order for this company, or `null`.
 *
 * THE `try` OPENS BEFORE `window.localStorage`, not around `getItem`: in a
 * private window, with site data blocked, or inside a thumbnail/preview
 * context, reading the property itself throws. A stored value is a
 * convenience; the page renders correctly with none.
 *
 * The parse is VALIDATED, not cast. Storage is user-writable and survives
 * deploys, so an older or hand-edited shape must come back as "nothing stored"
 * rather than as a sort key the table cannot express.
 */
export const readPortalSort = (
  companyId: string | null | undefined,
): PortalStoredSort | null => {
  if (!companyId) return null;
  try {
    const raw = window.localStorage.getItem(portalSortStorageKey(companyId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { sortBy, sortDir } = parsed as Partial<PortalStoredSort>;
    // `"risk"` IS DELIBERATELY ABSENT: the half of the RISK deletion that
    // lives in STORAGE. A reader who pressed RISK before it went still has
    // `{"sortBy":"risk"}` saved, and the rehydrate effect replays a stored pair
    // onto the URL, so accepting it here would resurrect the deleted order from
    // a key nothing writes any more. It returns as "nothing stored".
    if (sortBy !== "enforcement" && sortBy !== "projection") return null;
    if (sortDir !== "asc" && sortDir !== "desc") return null;
    return { sortBy, sortDir };
  } catch {
    return null;
  }
};

/**
 * Remember this company's chosen order. Silent on failure for
 * {@link readPortalSort}'s three reasons: a quota error, a blocked store, or an
 * accessor that throws must never take the page down over a convenience.
 */
export const writePortalSort = (
  companyId: string | null | undefined,
  sort: PortalStoredSort,
): void => {
  if (!companyId) return;
  try {
    window.localStorage.setItem(
      portalSortStorageKey(companyId),
      JSON.stringify(sort),
    );
  } catch {
    // Storage is unavailable; the reader simply picks their order again.
  }
};

/** Map a column's active direction to the ARIA `aria-sort` token. */
export const ariaSortFor = (
  column: PortalBuildingsSortBy,
  active: { sortBy: PortalBuildingsSortBy; sortDir: PortalBuildingsSortDir },
): "ascending" | "descending" | "none" => {
  if (column !== active.sortBy) return "none";
  return active.sortDir === "asc" ? "ascending" : "descending";
};
